import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { 
  Users, 
  TrendingUp, 
  AlertTriangle, 
  Zap, 
  CheckCircle,
  Clock,
  History
} from 'lucide-react';

import { PartnerContext } from '../App';

// ─── KPI Stat Card ────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div
    className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm flex flex-col gap-3"
    style={{ borderTop: `3px solid ${accent}` }}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">{label}</span>
      <Icon size={18} style={{ color: accent }} />
    </div>
    <p className="text-3xl font-black text-zinc-900 leading-none">{value}</p>
    {sub && <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">{sub}</p>}
  </div>
);

// ─── Blocker Tag Pill ─────────────────────────────────────────────────────────
const BlockerTag = ({ type }) => {
  const map = {
    infrastructure: { label: 'Infrastructure', color: '#ef4444' },
    socioeconomic:  { label: 'Socio-Economic', color: '#f59e0b' },
    personal:       { label: 'Personal / Health', color: '#3b82f6' },
  };
  const t = map[type?.toLowerCase()] || { label: type || 'Unknown', color: '#9ca3af' };
  return (
    <span
      className="text-white text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{ backgroundColor: t.color }}
    >
      {t.label}
    </span>
  );
};

// ─── Velocity Bar ─────────────────────────────────────────────────────────────
const VelocityBar = ({ label, value, max, color }) => (
  <div className="flex flex-col gap-1">
    <div className="flex justify-between items-center">
      <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">{label}</span>
      <span className="text-[11px] font-black text-zinc-800">{value}<span className="text-zinc-400 font-normal"> / {max}</span></span>
    </div>
    <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden">
      <div
        className="h-2 rounded-full transition-all duration-700"
        style={{ width: `${Math.min((value / max) * 100, 100)}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const { activePartner } = React.useContext(PartnerContext);
  
  const [kpi, setKpi] = useState({
    totalStudents: 0,
    activePipeline: 0,
    atRiskFlags: 0,
    infraBlockers: 0,
    loading: true,
  });

  const [blockerLog, setBlockerLog] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // ── Students ──────────────────────────────────────────────────────────
        let students = [];
        try {
          const studentsSnap = await getDocs(collection(db, 'students'));
          students = (studentsSnap.docs || []).map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn('[Dashboard] Error loading students:', e);
        }

        // Apply Global Geofence Filter
        if (activePartner !== 'global') {
          students = students.filter(s => s.partnerId === activePartner);
        }

        const totalStudents  = students.length;
        const activePipeline = students.filter(s => s.status === 'active').length;
        const atRiskFlags    = students.filter(s => s.riskFlag === true).length;

        // ── Blockers ──────────────────────────────────────────────────────────
        let blockers = [];
        try {
          const blockersSnap = await getDocs(collection(db, 'blockers'));
          blockers = (blockersSnap.docs || []).map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn('[Dashboard] Error loading blockers:', e);
        }
        const infraBlockers = blockers.filter(b => b.status === 'open').length;

        // Blocker exception log — students with active blocker tags
        const flagged = students
          .filter(s => s.blockerType && s.blockerStatus === 'open')
          .slice(0, 6)
          .map(s => ({
            name: s.name || s.fullName || 'Unknown Student',
            id: s.studentId || s.id,
            blockerType: s.blockerType,
            since: s.blockerDate || '—',
            velocityDrop: s.velocityDrop || '—',
          }));

        // ── Progress Events ───────────────────────────────────────────────────
        let eventsList = [];
        try {
          const eventsSnap = await getDocs(collection(db, 'progressEvents'));
          eventsList = (eventsSnap.docs || []).map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn('[Dashboard] Error loading events:', e);
        }
        
        eventsList.sort((a, b) => {
          const timeA = a.timestamp?.seconds || 0;
          const timeB = b.timestamp?.seconds || 0;
          return timeB - timeA;
        });

        const events = eventsList.slice(0, 5);

        setKpi({ totalStudents, activePipeline, atRiskFlags, infraBlockers, loading: false });
        setBlockerLog(flagged);
        setRecentEvents(events);
      } catch (err) {
        console.warn('[Dashboard] Render setup error:', err);
        setKpi(prev => ({ ...prev, loading: false }));
      }
    };

    fetchAll();
  }, [activePartner]);

  return (
    <div className="space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-black text-zinc-900 tracking-tight">Nexus Dashboard</h1>
        <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mt-1">
          Student Progress Velocity &amp; Operational Intelligence
        </p>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          label="Total Students"
          value={kpi.loading ? '—' : kpi.totalStudents}
          sub="Registered in registry"
          accent="#8B1A2D"
        />
        <KpiCard
          icon={TrendingUp}
          label="Active Pipeline"
          value={kpi.loading ? '—' : kpi.activePipeline}
          sub="Currently progressing"
          accent="#059669"
        />
        <KpiCard
          icon={AlertTriangle}
          label="At-Risk Flags"
          value={kpi.loading ? '—' : kpi.atRiskFlags}
          sub="Velocity drop detected"
          accent="#f59e0b"
        />
        <KpiCard
          icon={Zap}
          label="Infra Blockers"
          value={kpi.loading ? '—' : kpi.infraBlockers}
          sub="Open qualitative tags"
          accent="#3b82f6"
        />
      </div>

      {/* ── Main Grid ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Blocker Exception Log ────────────────────────────────────────── */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
              Blocker Exception Log
            </h2>
            <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">
              Qualitative Override Active
            </span>
          </div>
          {blockerLog.length === 0 ? (
            <div className="px-6 py-10 text-center flex flex-col items-center justify-center">
              <CheckCircle size={32} className="text-zinc-200 mb-2" />
              <p className="text-xs text-zinc-400 font-bold">No active blockers — all students on track</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="text-left px-6 py-3 text-[9px] font-black text-zinc-400 uppercase tracking-widest">Student</th>
                  <th className="text-left px-6 py-3 text-[9px] font-black text-zinc-400 uppercase tracking-widest">Blocker Type</th>
                  <th className="text-left px-6 py-3 text-[9px] font-black text-zinc-400 uppercase tracking-widest">Since</th>
                  <th className="text-left px-6 py-3 text-[9px] font-black text-zinc-400 uppercase tracking-widest">Velocity Δ</th>
                </tr>
              </thead>
              <tbody>
                {blockerLog.map((s, i) => (
                  <tr key={i} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-3">
                      <p className="font-bold text-zinc-900 text-xs">{s.name}</p>
                      <p className="text-[9px] text-zinc-400 font-mono">{s.id}</p>
                    </td>
                    <td className="px-6 py-3"><BlockerTag type={s.blockerType} /></td>
                    <td className="px-6 py-3 text-[10px] text-zinc-500 font-mono">{s.since}</td>
                    <td className="px-6 py-3 text-[10px] font-black text-red-500">{s.velocityDrop}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Velocity Summary ─────────────────────────────────────────────── */}
        <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-6 flex flex-col gap-6">
          <div>
            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 mb-1">
              Cohort Velocity Index
            </h2>
            <p className="text-[9px] text-zinc-400">ΔMilestones / Δt — VPRE algorithm</p>
          </div>

          <div className="flex flex-col gap-4">
            <VelocityBar label="Onboarding → Active"   value={kpi.activePipeline}  max={Math.max(kpi.totalStudents, 1)} color="#8B1A2D" />
            <VelocityBar label="Active → At-Risk"       value={kpi.atRiskFlags}     max={Math.max(kpi.activePipeline, 1)} color="#f59e0b" />
            <VelocityBar label="Open Infra Blockers"    value={kpi.infraBlockers}   max={Math.max(kpi.totalStudents, 1)} color="#3b82f6" />
          </div>

          <div className="mt-auto pt-4 border-t border-zinc-100">
            <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-widest mb-1">System Status</p>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
              <span className="text-xs font-black text-zinc-700">Nexus Engine Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Progress Event Log (VPRE-style append ledger) ──────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100">
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
            Progress Event Ledger
          </h2>
          <p className="text-[9px] text-zinc-400 mt-0.5">Append-only timestamped record — VPRE architecture</p>
        </div>
        {recentEvents.length === 0 ? (
          <div className="px-6 py-8 text-center flex flex-col items-center justify-center">
            <Clock size={32} className="text-zinc-200 mb-2" />
            <p className="text-xs text-zinc-400 font-bold">No events logged yet — ledger will populate as students submit reports</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {recentEvents.map((ev, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-3">
                <span className="text-[8px] font-black text-zinc-400 uppercase tracking-widest w-16 shrink-0">
                  {ev.eventType || 'EVENT'}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-zinc-600">{ev.description || 'Progress event recorded'}</span>
                  {ev.studentName && (
                    <span className="text-xs font-black text-zinc-900 ml-1">— {ev.studentName}</span>
                  )}
                </div>
                <span className="text-[9px] font-mono text-zinc-400 shrink-0">
                  {ev.timestamp && typeof ev.timestamp.toDate === 'function' 
                    ? ev.timestamp.toDate().toLocaleDateString() 
                    : ev.timestamp || '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
